import type {Component} from 'solid-js';
import {Show} from 'solid-js';
import imagePaypal from "/src/assets/images/paypal-logo.png";
import imageAppIcon from "../../assets/images/icon.png";
import imageScreenshot from "../../assets/images/Screenshot from 2023-04-13 09-36-412.png";
import './styles.less';
import {getClientOs} from "../utils";
import {AppleStoreBadge, GitHubBadge, GooglePlayBadge} from "../components/badges";
import {
    FaBrandsAndroid,
    FaBrandsApple,
    FaBrandsGithub,
    FaSolidBolt,
    FaSolidCloudArrowDown,
    FaSolidDownload,
    FaSolidHeart,
    FaSolidListCheck,
    FaSolidMagnifyingGlass,
    FaSolidMusic,
    FaSolidQuoteLeft,
    FaSolidSliders
} from "solid-icons/fa";

const HomePage: Component = () => {
    const clientOs = getClientOs();

    const getBadgeForClientOs = () => {
        switch (clientOs) {
            case "android":
                return <GooglePlayBadge/>;
            case "ios":
                return <AppleStoreBadge/>;
            case "windows":
            case "unknown":
            default:
                return null;
        }
    };

    const badgeForClientOs = getBadgeForClientOs();

    return (
        <div class="homepage-wrapper">
            {/* Top Navigation Bar */}
            <header class="home-nav">
                <div class="home-nav-container">
                    <a href="/" class="brand-link">
                        <img src={imageAppIcon} alt="Hymnbook Logo" class="brand-icon"/>
                        <span class="brand-text">Hymnbook</span>
                    </a>

                    <nav class="nav-links">
                        <a href="#features">Features</a>
                        <a href="#scripture">About</a>
                        <a href="#download">Download</a>
                        <a href="#donate" class="nav-donate-link">Donate</a>
                    </nav>

                    <a href="#download" class="nav-cta-btn">
                        <FaSolidDownload/>
                        <span>Get App</span>
                    </a>
                </div>
            </header>

            {/* Hero Section */}
            <section id="hero">
                <div class="hero-backdrop-glow"></div>
                <div class="container hero-container">
                    <div class="hero-content">
                        <div class="hero-tag">
                            <span class="pulse-dot"></span>
                            <span>100% Free & Open-Source Worship Companion</span>
                        </div>

                        <h1 class="hero-title">
                            Sing with joy.<br/>
                            <span class="highlight-text">Worship with clarity.</span>
                        </h1>

                        <p class="hero-description">
                            Welcome to Hymnbook — the modern, distraction-free mobile app that brings Christian song lyrics,
                            melodies, and worship sets into the palm of your hand. Fast, elegant, and simple.
                        </p>

                        <div class="hero-pills">
                            <span class="hero-pill"><FaSolidBolt/> Fast Number Lookup</span>
                            <span class="hero-pill"><FaSolidMusic/> Melody Player</span>
                            <span class="hero-pill"><FaSolidCloudArrowDown/> Offline Ready</span>
                        </div>

                        {/* Direct Download Badges */}
                        <div class="hero-actions">
                            <Show when={badgeForClientOs}>
                                <div class="primary-os-badge">
                                    <span class="recommended-badge-label">Recommended for your device:</span>
                                    {badgeForClientOs}
                                </div>
                            </Show>

                            <div class="store-badges-hero">
                                <Show when={!badgeForClientOs || clientOs !== "android"}>
                                    <GooglePlayBadge size="small"/>
                                </Show>
                                <Show when={!badgeForClientOs || clientOs !== "ios"}>
                                    <AppleStoreBadge size="small"/>
                                </Show>
                                <GitHubBadge size="small"/>
                            </div>
                        </div>
                    </div>

                    {/* Hero App Mockup Frame */}
                    <div class="hero-visual">
                        <div class="phone-frame">
                            <div class="phone-speaker"></div>
                            <div class="phone-screen">
                                <img
                                    src={imageScreenshot}
                                    alt="Hymnbook App Interface"
                                    class="screenshot-img"
                                />
                            </div>
                            {/* Floating Highlight Badges */}
                            <div class="float-badge badge-top-right">
                                <FaSolidMusic class="badge-icon icon-music"/>
                                <div class="badge-text">
                                    <strong>Melody Player</strong>
                                    <span>Subject to availability</span>
                                </div>
                            </div>
                            <div class="float-badge badge-bottom-left">
                                <FaSolidBolt class="badge-icon icon-bolt"/>
                                <div class="badge-text">
                                    <strong>Instant Number Pad</strong>
                                    <span>Jump straight to verse</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Scripture & Heart Section */}
            <section id="scripture" class="scripture-section">
                <div class="container">
                    <div class="scripture-card">
                        <span class="quote-icon"><FaSolidQuoteLeft/></span>
                        <blockquote class="scripture-quote">
                            "Als de HERE de bouw van een huis niet zegent, is alle moeite nutteloos."
                        </blockquote>
                        <cite class="scripture-source">Psalmen 127: 1a (HTB)</cite>
                        <div class="scripture-divider"></div>
                        <p class="mission-text">
                            Hymnbook was born out of a desire to make access to Christian hymns and worship songs
                            effortless for everyone with a digital device. Whether you are leading a church service,
                            gathering with family, singing in youth group, or in personal quiet time, Hymnbook's goal is
                            to support you in praising the Lord in spirit and truth.
                        </p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" class="features-section">
                <div class="container">
                    <div class="section-header">
                        <span class="section-eyebrow">Features</span>
                        <h2>Crafted for Effortless Worship</h2>
                        <p class="section-subtext">
                            Everything you need to find songs in seconds, sing in harmony, and lead congregational worship with confidence.
                        </p>
                    </div>

                    <div class="features-grid">
                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidBolt/>
                            </div>
                            <h3>Instant Song Selection</h3>
                            <p>
                                Jump straight to any song using the immediate number keypad or bundle selector.
                                No tedious scrolling required when numbers are announced during service.
                            </p>
                        </div>

                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidMusic/>
                            </div>
                            <h3>Melodies & Sheet Music</h3>
                            <p>
                                Unsure how the tune goes? View musical notation notes right above the lyrics or play the audio melody
                                with customizable playback tempo.
                            </p>
                            <div class="feature-disclaimer">
                                <em>Note:</em> Melodies and sheet music are actively in development and subject to availability per song.
                            </div>
                        </div>

                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidMagnifyingGlass/>
                            </div>
                            <h3>Full-Text Lyrics Search</h3>
                            <p>
                                Search through thousands of titles, verse lines, and chorus phrases. Find that exact hymn even
                                when you only remember a fragment of the lyrics.
                            </p>
                        </div>

                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidListCheck/>
                            </div>
                            <h3>Worship Song Lists</h3>
                            <p>
                                Prepare and organize setlists in advance for Sunday services, prayer meetings, or choir practices.
                                Swipe through songs sequentially with ease.
                            </p>
                        </div>

                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidCloudArrowDown/>
                            </div>
                            <h3>100% Offline Access</h3>
                            <p>
                                No internet connection in the church sanctuary or retreat camp? No problem.
                                All your favorite songs and bundles stay downloaded and fully accessible offline.
                            </p>
                        </div>

                        <div class="feature-card">
                            <div class="feature-icon-box">
                                <FaSolidSliders/>
                            </div>
                            <h3>Tailored Reading Experience</h3>
                            <p>
                                Adjust font scaling, melody sizes, verse highlighting, and switch between light and dark themes
                                for seamless viewing in any lighting condition.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Download Section */}
            <section id="download" class="download-section">
                <div class="container">
                    <div class="section-header">
                        <span class="section-eyebrow">Availability</span>
                        <h2>Get Hymnbook Today</h2>
                        <p class="section-subtext">
                            Available on Android and iOS. Free to download, free of advertisements, and built for everyone.
                        </p>
                    </div>

                    <div class="download-cards-container">
                        {/* Android Card */}
                        <div class="download-card">
                            <div class="platform-icon">
                                <FaBrandsAndroid/>
                            </div>
                            <h3>Android</h3>
                            <p>Compatible with phones and tablets running Android 6.0 and above.</p>
                            <div class="download-badge-wrapper">
                                <GooglePlayBadge/>
                            </div>
                        </div>

                        {/* iOS Card */}
                        <div class="download-card">
                            <div class="platform-icon">
                                <FaBrandsApple/>
                            </div>
                            <h3>iPhone & iPad</h3>
                            <p>Available on the Apple App Store for all modern iOS and iPadOS devices.</p>
                            <div class="download-badge-wrapper">
                                <AppleStoreBadge/>
                            </div>
                        </div>

                        {/* Direct GitHub Bundle */}
                        <div class="download-card">
                            <div class="platform-icon">
                                <FaBrandsGithub/>
                            </div>
                            <h3>Open Source & APK</h3>
                            <p>Prefer to sideload or inspect the source code? Download direct builds from GitHub.</p>
                            <div class="download-badge-wrapper">
                                <GitHubBadge/>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Donate Section */}
            <section id="donate" class="donate-section">
                <div class="container">
                    <div class="donate-card">
                        <div class="donate-header">
                            <div class="heart-icon-wrapper">
                                <FaSolidHeart/>
                            </div>
                            <h2>Support the Ministry</h2>
                            <p class="donate-description">
                                Hymnbook is completely free and non-profit, built solely to bless Christians and congregations worldwide.
                                There are no subscriptions, paywalls, or advertisements. All server hosting, app store fees, and development
                                costs are supported purely through voluntary gifts.
                            </p>
                        </div>

                        <div class="donate-links-grid">
                            <a
                                href="https://www.buymeacoffee.com/sajansen"
                                target="_blank"
                                rel="noreferrer"
                                class="donation-btn buymeacoffee-btn"
                            >
                                <span class="btn-avatar">
                                    <img
                                        class="donate-logo"
                                        src="https://cdn.buymeacoffee.com/uploads/profile_pictures/default/5F7FFF/SJ.png"
                                        alt="Buy me a coffee"
                                    />
                                </span>
                                <span class="btn-text">
                                    <strong>Buy Me a Coffee</strong>
                                    <small>One-time or monthly support</small>
                                </span>
                            </a>

                            <a
                                href="https://www.paypal.com/donate/?hosted_button_id=6KTU5JNVS699E"
                                target="_blank"
                                rel="noreferrer"
                                class="donation-btn paypal-btn"
                            >
                                <span class="btn-avatar">
                                    <img class="donate-logo" src={imagePaypal} alt="PayPal Donation"/>
                                </span>
                                <span class="btn-text">
                                    <strong>Donate via PayPal</strong>
                                    <small>Secure credit card or balance</small>
                                </span>
                            </a>
                        </div>

                        <p class="donate-note">
                            Every contribution, no matter the size, helps keep the app maintained and accessible for worshipers worldwide.
                            Thank you for your kindness and prayers!
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
